import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import SEOHead from '../components/SEOHead';
import { 
  Radio, 
  Server, 
  GraduationCap, 
  CheckCircle, 
  ArrowRight, 
  Phone,
  Wifi,
  Database,
  Shield,
  BarChart3,
  Cpu
} from 'lucide-react';
import { services, serviceImages } from '../mock';
import { Link } from 'react-router-dom';

const ServicesPage = () => {
  const detailedServices = {
    telecoms: {
      ...services.telecoms,
      icon: Radio,
      color: 'blue',
      features: [
        {
          icon: Wifi,
          title: "Ingénierie Radio 2G/3G/4G",
          description: "Planification, design et optimisation complète des réseaux mobiles avec les dernières technologies.",
          image: serviceImages.radioEngineering
        },
        {
          icon: BarChart3,
          title: "Audit & Benchmarking QoS/QoE",
          description: "Mesures comparatives des performances et analyse détaillée de la qualité de service.",
          image: serviceImages.auditQos
        },
        {
          icon: Phone,
          title: "Installation & Rollout",
          description: "Déploiement d'équipements télécoms et mise en service de sites radio.",
          image: serviceImages.rollout
        }
      ]
    },
    informatique: {
      ...services.informatique,
      icon: Server,
      color: 'orange',
      features: [
        {
          icon: Database,
          title: "Data Center & Virtualisation",
          description: "Architecture, implémentation et gestion de centres de données modernes.",
          image: serviceImages.dataCenter
        },
        {
          icon: Shield,
          title: "Ingénierie Télécoms",
          description: "Conception et optimisation des systèmes de télécommunications avec expertise technique avancée.",
          image: serviceImages.heroEmployee
        },
        {
          icon: Cpu,
          title: "Plateformes Collaboratives",
          description: "Conception et déploiement de solutions collaboratives d'entreprise.",
          image: serviceImages.collaborative
        }
      ]
    },
    formations: {
      ...services.formations,
      icon: GraduationCap,
      color: 'green',
      features: [
        {
          icon: Radio,
          title: "Formations Télécoms",
          description: "Formation technique sur les architectures, normes et optimisation des réseaux.",
          image: serviceImages.trainingTelecom
        },
        {
          icon: Server,
          title: "Formations IT",
          description: "Programmes de formation sur les réseaux, bases de données et sécurité informatique.",
          image: serviceImages.trainingIT
        },
        {
          icon: BarChart3,
          title: "Conseil Stratégique",
          description: "Accompagnement dans la transformation digitale et l'optimisation des processus.",
          image: serviceImages.conseilStrategique
        }
      ]
    }
  };

  const getColorClasses = (color) => {
    const colors = {
      blue: {
        bg: 'bg-blue-50',
        border: 'border-blue-200',
        text: 'text-blue-600',
        button: 'bg-blue-600 hover:bg-blue-700'
      },
      orange: {
        bg: 'bg-orange-50',
        border: 'border-orange-200', 
        text: 'text-orange-600',
        button: 'bg-orange-600 hover:bg-orange-700'
      },
      green: {
        bg: 'bg-green-50',
        border: 'border-green-200',
        text: 'text-green-600', 
        button: 'bg-green-600 hover:bg-green-700'
      }
    };
    return colors[color];
  };

  const siteUrl = "https://www.set-g.com";

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Service",
    "@id": `${siteUrl}/services#service`,
    "name": "Services Télécommunications et IT - SET-G",
    "description": "Services complets en audit QoS, ingénierie radio, infrastructure IT et formation professionnelle par SET-G en Côte d'Ivoire.",
    "provider": {
      "@type": "Organization",
      "name": "SET-G",
      "url": siteUrl
    },
    "areaServed": {
      "@type": "Country",
      "name": "Côte d'Ivoire"
    },
    "serviceType": ["Audit QoS", "Ingénierie Radio", "Infrastructure IT", "Formation Télécoms"],
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Catalogue des Services SET-G",
      "itemListElement": Object.entries(detailedServices).map((service, index) => ({
        "@type": "Offer",
        "position": index + 1,
        "itemOffered": {
          "@type": "Service",
          "name": service[1].title,
          "description": service[1].description
        }
      }))
    }
  };

  return (
    <>
      <SEOHead
        title="Services Télécoms & IT | Audit QoS, Ingénierie Radio, Infrastructure"
        description="Services SET-G : Audit QoS/QoE réseaux mobiles, ingénierie radio 2G/3G/4G, infrastructure IT & data center, formation télécoms. Expert ARTCI en Côte d'Ivoire. Devis gratuit ☎ +225 27 22 34 98 15"
        keywords="audit QoS Côte d'Ivoire, ingénierie radio 2G 3G 4G, benchmarking QoS, infrastructure IT Abidjan, formation télécommunications, data center virtualisation, rollout télécoms, conseil IT Afrique"
        structuredData={structuredData}
      />

      <div className="min-h-screen bg-slate-50">
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-slate-900 to-slate-800 text-white py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center max-w-4xl mx-auto">
              <div className="inline-flex items-center bg-orange-100 text-orange-800 px-4 py-2 rounded-full text-sm font-medium mb-6">
                Nos Expertises
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
                Services & Solutions
              </h1>
              <p className="text-xl text-slate-300 leading-relaxed">
                Découvrez notre gamme complète de services en télécommunications, 
                informatique et formation professionnelle pour accompagner votre croissance.
              </p>
            </div>
          </div>
        </section>

        {/* Services détaillés */}
        <section className="py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="space-y-20">
              {Object.entries(detailedServices).map(([key, service]) => {
                const Icon = service.icon;
                const colors = getColorClasses(service.color);
                
                return (
                  <div key={key} className="">
                    {/* Service Header */}
                    <div className="text-center mb-16">
                      <div className={`inline-flex items-center justify-center w-20 h-20 ${colors.bg} rounded-full mb-6`}>
                        <Icon className={`w-10 h-10 ${colors.text}`} />
                      </div>
                      <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-4">
                        {service.title}
                      </h2>
                      <p className="text-xl text-slate-600 max-w-3xl mx-auto">
                        {service.description}
                      </p>
                    </div>

                    {/* Features Grid */}
                    <div className="grid lg:grid-cols-3 gap-8 mb-12">
                      {service.features.map((feature, index) => {
                        const FeatureIcon = feature.icon;
                        return (
                          <Card key={index} className="hover:shadow-lg transition-shadow duration-300 overflow-hidden">
                            {/* Feature Image */}
                            {feature.image && (
                              <div className="relative h-48 overflow-hidden">
                                <img 
                                  src={feature.image} 
                                  alt={`${feature.title} - ${feature.description} - Service SET-G Côte d'Ivoire`}
                                  title={feature.title}
                                  loading="lazy"
                                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent"></div>
                                <div className="absolute bottom-4 left-4">
                                  <div className={`w-10 h-10 bg-white/90 rounded-lg flex items-center justify-center shadow-md`}>
                                    <FeatureIcon className={`w-5 h-5 ${colors.text}`} />
                                  </div>
                                </div>
                              </div>
                            )}
                            
                            <CardHeader>
                              {!feature.image && (
                                <div className={`w-12 h-12 ${colors.bg} rounded-lg flex items-center justify-center mb-4`}>
                                  <FeatureIcon className={`w-6 h-6 ${colors.text}`} />
                                </div>
                              )}
                              <CardTitle className="text-xl font-bold text-slate-900">
                                {feature.title}
                              </CardTitle>
                            </CardHeader>
                            <CardContent>
                              <CardDescription className="text-slate-600 leading-relaxed">
                                {feature.description}
                              </CardDescription>
                            </CardContent>
                          </Card>
                        );
                      })}
                    </div>

                    {/* Complete service list */}
                    <Card className={`${colors.bg} ${colors.border} border-2`}>
                      <CardHeader>
                        <CardTitle className="text-2xl font-bold text-slate-900 mb-4">
                          Services Détaillés - {service.title}
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="grid md:grid-cols-2 gap-4">
                          {service.items.map((item, index) => (
                            <div key={index} className="flex items-start space-x-3">
                              <CheckCircle className="w-5 h-5 text-green-600 mt-1 flex-shrink-0" />
                              <span className="text-slate-700 leading-relaxed">{item}</span>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-slate-900">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl lg:text-4xl font-bold text-white mb-6">
              Prêt à Optimiser vos Systèmes ?
            </h2>
            <p className="text-xl text-slate-300 mb-8 leading-relaxed">
              Contactez nos experts pour un devis personnalisé et découvrez comment nous pouvons 
              améliorer vos performances techniques et opérationnelles.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                asChild
                size="lg"
                className="bg-orange-600 hover:bg-orange-700 text-white px-8 py-4 text-lg font-medium"
              >
                <Link to="/contact">
                  Demander un Devis
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Link>
              </Button>
              <Button 
                asChild
                variant="outline" 
                size="lg"
                className="border-white text-white hover:bg-white hover:text-slate-900 px-8 py-4 text-lg font-medium"
              >
                <Link to="/projets">
                  Voir nos Projets
                </Link>
              </Button>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default ServicesPage;